import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal } from '@evertrue/et-components';
import EverTrue from 'app';
import UserSelector from 'apps/users/components/user-selector';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import useCadenceMutations from '../../hooks/use-cadence-mutations';

/**
 * Google Drive-style sharing modal for cadences.
 * Shows existing shares, lets you add/edit/revoke per-user permissions.
 */
const CadenceSharingModal = ({ isOpen, onClose, cadenceId, cadenceVersion: initialVersion, cadenceName, ownerId }) => {
  const { shareCadence, updateCadenceSharing, revokeCadenceSharing, fetchCadenceSharingSettings } =
    useCadenceMutations();

  const [shares, setShares] = useState([]);
  const [resolvedUsers, setResolvedUsers] = useState({});
  const [loadingShares, setLoadingShares] = useState(false);
  const [version, setVersion] = useState(initialVersion);

  // Add-user form state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newPermission, setNewPermission] = useState('view');
  const [busy, setBusy] = useState(false);

  const currentUserId = EverTrue.store?.user?.get('id');

  /** Fetch existing shares and resolve user names (including owner). */
  const loadShares = async () => {
    if (!cadenceId) return;
    setLoadingShares(true);
    try {
      const settings = await fetchCadenceSharingSettings(cadenceId);
      setShares(settings);

      // Resolve user names for all share records + owner
      const userIds = settings.map((s) => s.userId).filter(Boolean);
      if (ownerId) userIds.push(ownerId);
      if (userIds.length > 0) {
        const uniqueIds = [...new Set(userIds)];
        try {
          const users = await fetchUsersById(uniqueIds);
          const map = {};
          (users || []).forEach((u) => {
            map[u.id] = u.name || u.email || `User ${u.id}`;
          });
          setResolvedUsers(map);
        } catch {
          // If user resolution fails, we'll show "User {id}" as fallback
        }
      }
    } catch (err) {
      console.warn('[cadences] Failed to load sharing settings:', err.message);
    } finally {
      setLoadingShares(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setVersion(initialVersion);
      loadShares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cadenceId]);

  /** Add new per-user shares. */
  const handleAddShare = async () => {
    if (selectedUsers.length === 0) return;
    setBusy(true);
    let latestVersion = version;

    try {
      for (const user of selectedUsers) {
        const userId = typeof user === 'object' ? user.value : user;
        const result = await shareCadence({
          cadenceId: Number(cadenceId),
          cadenceVersion: latestVersion,
          userId: Number(userId),
          canView: true,
          canEdit: newPermission === 'edit',
        });
        if (result?.cadenceVersion) latestVersion = result.cadenceVersion;
      }
      setVersion(latestVersion);
      setSelectedUsers([]);
      await loadShares();
    } catch (err) {
      EverTrue.Alert.error(`Share failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  /** Change permission for an existing share record. */
  const handleChangePermission = async (share, newLevel) => {
    setBusy(true);
    try {
      const result = await updateCadenceSharing(share.id, cadenceId, version, {
        canView: true,
        canEdit: newLevel === 'edit',
      });
      if (result?.cadenceVersion) setVersion(result.cadenceVersion);
      await loadShares();
    } catch (err) {
      EverTrue.Alert.error(`Update failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  /** Revoke a user's access. */
  const handleRevoke = async (share) => {
    setBusy(true);
    try {
      const result = await revokeCadenceSharing(share.id, cadenceId, version);
      if (result?.cadenceVersion) setVersion(result.cadenceVersion);
      await loadShares();
    } catch (err) {
      EverTrue.Alert.error(`Revoke failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const getUserName = (userId) => {
    if (userId === currentUserId) return `${resolvedUsers[userId] || 'You'} (you)`;
    return resolvedUsers[userId] || `User ${userId}`;
  };

  const getInitials = (userId) => {
    const name = resolvedUsers[userId];
    if (!name) return '?';
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose} closeOnOutsideClick>
      <div className="cadence-sharing-modal">
        <div className="cadence-sharing-modal--header">
          <h3 className="cadence-sharing-modal--title">Share &ldquo;{cadenceName}&rdquo;</h3>
          <button type="button" className="cadence-sharing-modal--close-btn" onClick={onClose} aria-label="Close">
            <Icon icon="close" />
          </button>
        </div>

        {/* Add people row */}
        <div className="cadence-sharing-modal--add-row">
          <div className="cadence-sharing-modal--user-picker">
            <UserSelector selected={selectedUsers} onChange={setSelectedUsers} />
          </div>
          <select
            className="cadence-sharing-modal--permission-select"
            value={newPermission}
            onChange={(e) => setNewPermission(e.target.value)}
          >
            <option value="view">Can view</option>
            <option value="edit">Can edit</option>
          </select>
          <Button type="action" onClick={handleAddShare} disabled={selectedUsers.length === 0 || busy}>
            Share
          </Button>
        </div>

        {/* People with access */}
        <div className="cadence-sharing-modal--divider">
          <span>People with access</span>
        </div>

        {loadingShares ? (
          <div className="cadence-sharing-modal--loading">
            <Loading />
          </div>
        ) : (
          <div className="cadence-sharing-modal--access-list">
            {/* Owner row (always first, not editable) */}
            {ownerId && (
              <div className="cadence-sharing-modal--access-row">
                <span className="cadence-sharing-modal--avatar">{getInitials(ownerId)}</span>
                <span className="cadence-sharing-modal--user-name">{getUserName(ownerId)}</span>
                <span className="cadence-sharing-modal--owner-label">Owner</span>
              </div>
            )}

            {/* Shared users */}
            {shares
              .filter((s) => s.userId !== ownerId)
              .map((share) => (
                <div key={share.id} className="cadence-sharing-modal--access-row">
                  <span className="cadence-sharing-modal--avatar">{getInitials(share.userId)}</span>
                  <span className="cadence-sharing-modal--user-name">{getUserName(share.userId)}</span>
                  <select
                    className="cadence-sharing-modal--permission-select"
                    value={share.canEdit ? 'edit' : 'view'}
                    onChange={(e) => handleChangePermission(share, e.target.value)}
                    disabled={busy}
                  >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                  </select>
                  <button
                    type="button"
                    className="cadence-sharing-modal--remove-btn"
                    onClick={() => handleRevoke(share)}
                    disabled={busy}
                    title="Remove access"
                  >
                    <Icon icon="close" />
                  </button>
                </div>
              ))}

            {shares.filter((s) => s.userId !== ownerId).length === 0 && (
              <p className="cadence-sharing-modal--empty">No one else has access yet.</p>
            )}
          </div>
        )}

        <div className="cadence-sharing-modal--footer">
          <Button type="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

CadenceSharingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cadenceVersion: PropTypes.number,
  cadenceName: PropTypes.string,
  ownerId: PropTypes.number,
};

export default CadenceSharingModal;
